/* @flow */
import * as React from 'react';
import { observable, computed, reaction } from 'mobx';
import { observer } from 'mobx-react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';

import Button from '@material-ui/core/Button';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import LinearProgress from '@material-ui/core/LinearProgress';

import type { ImportSectionPreview, ImportSectionResult } from 'api/brandsBulkImport';
import navigation from 'services/NavigationService';
import PageWrapper from 'components/PageWrapper/PageWrapper';
import FormWrapper from 'components/FormWrapper/FormWrapper';
import FormSection from 'components/FormSection/FormSection';
import ButtonGroup from 'components/ButtonGroup/ButtonGroup';
import ElectroResponseMessage from 'forms/components/inputs/ElectroResponseMessage';
import ElectroCheckbox from 'forms/components/inputs/ElectroCheckbox/ElectroCheckbox';
import ElectroFileTransfer from 'forms/components/inputs/ElectroFileTransfer';
import FormControls from 'forms/components/controls/FormControls/FormControls';

import MultipleBrandUploadController from './MultipleBrandUploadController';
import { makeFields, multipleBrandUpload } from './setUpMultipleBrandForm';
import ImportSummary from './ImportSummary';
import ImportRowList from './ImportRowList';
import ImportErrorReport from './ImportErrorReport';
import ImportCreatedWithoutAccounts from './ImportCreatedWithoutAccounts';
import styles from './MultipleBrandForm.module.scss';

const panelSummaryClasses = {
  expandIcon: styles['panel-expand-icon'],
  expanded: styles['panel-expanded'],
};
const panelCollapseProps = { timeout: 0 };

const ExpandIcon = (): React.Element<'svg'> => (
  <svg className={styles['expand-icon']} aria-hidden="true">
    <use xlinkHref="#icon-chevron-down" />
  </svg>
);

type PanelSectionProps = {|
  title: React.Node,
  children: React.Node,
  defaultExpanded?: boolean,
  titleClassName?: string,
|};

const PanelSection = ({
  title,
  children,
  defaultExpanded = false,
  titleClassName = styles['summary-text'],
}: PanelSectionProps): React.Node => (
  <ExpansionPanel
    className={styles.panel}
    CollapseProps={panelCollapseProps}
    defaultExpanded={defaultExpanded}
  >
    <ExpansionPanelSummary
      className={styles['panel-summary']}
      classes={panelSummaryClasses}
      expandIcon={<ExpandIcon />}
    >
      <span className={titleClassName}>{title}</span>
    </ExpansionPanelSummary>
    <ExpansionPanelDetails className={styles['panel-details']}>{children}</ExpansionPanelDetails>
  </ExpansionPanel>
);

PanelSection.defaultProps = {
  defaultExpanded: false,
  titleClassName: styles['summary-text'],
};

type TypeProps = {||} & TranslatorProps;

const STAGE_LABEL_SUFFIXES = {
  brands: 'stage_brands',
  accounts: 'stage_accounts',
  refreshing_accounts_summary: 'stage_refreshing_accounts_summary',
  refreshing_reporting_stats_cache: 'stage_refreshing_reporting_stats_cache',
};

@translate()
@observer
export default class MultipleBrandForm extends React.Component<TypeProps> {
  controller: MultipleBrandUploadController = new MultipleBrandUploadController();

  @observable localState = {
    responseMessage: '',
    showResponseMessage: false,
    isLoading: false,
    hasSuccess: false,
    hasError: false,
  };

  disposeErrorReaction: ?() => void = null;

  componentDidMount() {
    this.disposeErrorReaction = reaction(
      () => this.controller.errorMessage,
      (errorMessage: ?string) => {
        if (errorMessage) {
          this.localState.hasError = true;
          this.localState.hasSuccess = false;
          this.localState.showResponseMessage = true;
          this.localState.responseMessage = errorMessage;
        } else {
          this.localState.showResponseMessage = false;
          this.localState.responseMessage = '';
          this.localState.hasError = false;
          this.localState.hasSuccess = false;
        }
      }
    );
  }

  componentWillUnmount() {
    if (this.disposeErrorReaction) {
      this.disposeErrorReaction();
    }
    this.controller.dispose();
  }

  @computed
  get form(): Object {
    const { t }: TypeProps = this.props;
    const fields = makeFields(t);

    return multipleBrandUpload(fields, this.localState, this.controller, t);
  }

  handleConfirm = () => {
    this.controller.confirmUpload();
  };

  handleBack = () => {
    this.controller.backToUpload();
  };

  handleDone = () => {
    navigation.goBack();
  };

  getStageMessage(currentStage: ?string): string {
    const { t }: TypeProps = this.props;
    const suffix = currentStage && STAGE_LABEL_SUFFIXES[currentStage];

    return suffix
      ? t(`general:reporting.brand_form.multiple.labels.${suffix}`)
      : t('general:reporting.brand_form.multiple.messages.processing_description');
  }

  clearFileWhenUnchecked = (fieldPath: string) => (checkboxField: Object) => {
    if (!checkboxField.value) {
      this.form.$(fieldPath).set('');
    }
  };

  hasResultData(section: ?ImportSectionResult): boolean {
    return section != null && section.file_name != null;
  }

  renderInstructions(form: Object): React.Element<*> {
    const { t }: TypeProps = this.props;

    return (
      <FormSection form={form} section="instructions">
        <p>{t('general:reporting.brand_form.multiple.messages.instructions')}</p>
      </FormSection>
    );
  }

  renderUploadForm(): React.Element<*> {
    const { t }: TypeProps = this.props;
    const { form } = this;

    return (
      <PageWrapper isFlex={false}>
        <ElectroResponseMessage localState={this.localState} />
        <FormWrapper form={form}>
          {this.renderInstructions(form)}
          <FormSection form={form} section="uploadBrands">
            <ElectroCheckbox
              field={form.$('uploadBrands.includeBrands')}
              onChange={this.clearFileWhenUnchecked('uploadBrands.brandsFile')}
            />
            {form.$('uploadBrands.includeBrands').value && (
              <ElectroFileTransfer field={form.$('uploadBrands.brandsFile')} variant="upload" />
            )}
          </FormSection>
          <FormSection form={form} section="uploadTradingAccounts">
            <ElectroCheckbox
              field={form.$('uploadTradingAccounts.includeTradingAccounts')}
              onChange={this.clearFileWhenUnchecked('uploadTradingAccounts.tradingAccountsFile')}
            />
            {form.$('uploadTradingAccounts.includeTradingAccounts').value && (
              <ElectroFileTransfer
                field={form.$('uploadTradingAccounts.tradingAccountsFile')}
                variant="upload"
              />
            )}
          </FormSection>
          <FormControls
            buttonText={t('general:actions.preview')}
            form={form}
            disabled={form.submitting}
          />
        </FormWrapper>
      </PageWrapper>
    );
  }

  renderPreviewSection(section: ?ImportSectionPreview, type: 'brands' | 'accounts'): React.Node {
    if (!section) {
      return null;
    }

    const { t }: TypeProps = this.props;

    return (
      <div className={styles['preview-section']}>
        <h4>{t('general:reporting.brand_form.multiple.labels.file_heading')}</h4>
        <p>{section.file_name}</p>
        <h4>{t('general:reporting.brand_form.multiple.labels.summary_heading')}</h4>
        <ImportSummary section={section} t={t} />
        {section.rows.length > 0 && (
          <PanelSection
            title={t('general:reporting.brand_form.multiple.labels.rows_heading')}
            defaultExpanded={false}
          >
            <ImportRowList rows={section.rows} type={type} t={t} />
          </PanelSection>
        )}
        {section.warnings.length > 0 && (
          <PanelSection
            title={t('general:reporting.brand_form.multiple.labels.warnings_heading')}
            defaultExpanded
          >
            <ImportErrorReport errors={section.warnings} variant="warning" t={t} />
          </PanelSection>
        )}
        {section.errors.length > 0 && (
          <PanelSection
            title={t('general:reporting.brand_form.multiple.labels.errors_heading')}
            defaultExpanded
          >
            <ImportErrorReport errors={section.errors} variant="error" t={t} />
          </PanelSection>
        )}
      </div>
    );
  }

  renderPreview(): React.Element<*> {
    const { t }: TypeProps = this.props;
    const { form } = this;
    const { previewResult, isLoading } = this.controller;
    const hasNoSections = previewResult && !previewResult.brands && !previewResult.tradingAccounts;
    return (
      <PageWrapper isFlex={false}>
        <FormWrapper
          form={form}
          title={t('general:reporting.brand_form.multiple.labels.preview_title')}
        >
          <ElectroResponseMessage localState={this.localState} />
          <p>{t('general:reporting.brand_form.multiple.messages.preview_description')}</p>
          {previewResult && previewResult.brands && (
            <PanelSection
              title={t('general:reporting.brand_form.multiple.labels.upload_brands')}
              titleClassName={styles['section-summary-text']}
              defaultExpanded
            >
              {this.renderPreviewSection(previewResult.brands, 'brands')}
            </PanelSection>
          )}
          {previewResult && previewResult.tradingAccounts && (
            <PanelSection
              title={t('general:reporting.brand_form.multiple.labels.upload_trading_accounts')}
              titleClassName={styles['section-summary-text']}
              defaultExpanded
            >
              {this.renderPreviewSection(previewResult.tradingAccounts, 'accounts')}
            </PanelSection>
          )}
          {hasNoSections && (
            <p>{t('general:reporting.brand_form.multiple.messages.no_preview_rows')}</p>
          )}
          <ButtonGroup>
            <Button
              color="secondary"
              variant="contained"
              disabled={isLoading}
              onClick={this.handleBack}
            >
              {t('general:actions.back')}
            </Button>
            <Button
              color="primary"
              variant="contained"
              disabled={isLoading}
              onClick={this.handleConfirm}
            >
              {t('general:actions.confirm')}
            </Button>
          </ButtonGroup>
        </FormWrapper>
      </PageWrapper>
    );
  }

  renderPreviewing(): React.Element<*> {
    const { t }: TypeProps = this.props;
    const { form } = this;

    return (
      <PageWrapper isFlex={false}>
        <FormWrapper
          form={form}
          title={t('general:reporting.brand_form.multiple.labels.previewing_title')}
        >
          <ElectroResponseMessage localState={this.localState} />
          <p>{t('general:reporting.brand_form.multiple.messages.previewing_description')}</p>
          <LinearProgress variant="indeterminate" />
        </FormWrapper>
      </PageWrapper>
    );
  }

  renderProcessing(): React.Element<*> {
    const { t }: TypeProps = this.props;
    const { form } = this;
    const { jobStatus } = this.controller;

    return (
      <PageWrapper isFlex={false}>
        <FormWrapper
          form={form}
          title={t('general:reporting.brand_form.multiple.labels.processing_title')}
        >
          <ElectroResponseMessage localState={this.localState} />
          <p>{this.getStageMessage(jobStatus && jobStatus.currentStage)}</p>
          <LinearProgress variant="indeterminate" />
        </FormWrapper>
      </PageWrapper>
    );
  }

  renderResultSection(
    section: ImportSectionResult,
    type: 'brands' | 'accounts',
    hasAccountsFile: boolean = false
  ): React.Node {
    const { t }: TypeProps = this.props;

    return (
      <div className={styles['preview-section']}>
        <h4>{t('general:reporting.brand_form.multiple.labels.file_heading')}</h4>
        <p>{section.file_name}</p>
        <h4>{t('general:reporting.brand_form.multiple.labels.summary_heading')}</h4>
        <ImportSummary section={section} t={t} />
        {section.errors && section.errors.length > 0 && (
          <PanelSection
            title={t('general:reporting.brand_form.multiple.labels.errors_heading')}
            defaultExpanded
          >
            <ImportErrorReport errors={section.errors} variant="error" t={t} />
          </PanelSection>
        )}
        {type === 'brands' &&
          hasAccountsFile &&
          section.created_without_accounts &&
          section.created_without_accounts.length > 0 && (
            <ImportCreatedWithoutAccounts brands={section.created_without_accounts} t={t} />
          )}
      </div>
    );
  }

  renderResult(): React.Element<*> {
    const { t }: TypeProps = this.props;
    const { form } = this;
    const { jobStatus } = this.controller;
    const status = jobStatus && jobStatus.status;
    const hasBrandsData = Boolean(jobStatus) && this.hasResultData(jobStatus.brands);
    const hasTradingAccountsData =
      Boolean(jobStatus) && this.hasResultData(jobStatus.tradingAccounts);
    const hasNoSections = jobStatus && !hasBrandsData && !hasTradingAccountsData;
    return (
      <PageWrapper isFlex={false}>
        <FormWrapper
          form={form}
          title={t('general:reporting.brand_form.multiple.labels.result_title')}
        >
          <ElectroResponseMessage localState={this.localState} />
          {hasBrandsData && (
            <PanelSection
              title={t('general:reporting.brand_form.multiple.labels.upload_brands')}
              titleClassName={styles['section-summary-text']}
              defaultExpanded
            >
              {this.renderResultSection(jobStatus.brands, 'brands', hasTradingAccountsData)}
            </PanelSection>
          )}
          {hasTradingAccountsData && (
            <PanelSection
              title={t('general:reporting.brand_form.multiple.labels.upload_trading_accounts')}
              titleClassName={styles['section-summary-text']}
              defaultExpanded
            >
              {this.renderResultSection(jobStatus.tradingAccounts, 'accounts')}
            </PanelSection>
          )}
          {hasNoSections && (
            <p>{t('general:reporting.brand_form.multiple.messages.no_result_rows')}</p>
          )}
          {status === 'completed_with_errors' && (
            <div className={styles['result-warning']}>
              <p className={styles['result-warning__title']}>
                {t('general:reporting.brand_form.multiple.messages.completed_with_errors')}
              </p>
              <p className={styles['result-warning__description']}>
                {t(
                  'general:reporting.brand_form.multiple.messages.completed_with_errors_description'
                )}
              </p>
            </div>
          )}
          {status === 'failed' && (
            <p className={styles['result-error']}>
              {(jobStatus && jobStatus.failureMessage) ||
                t('general:reporting.brand_form.multiple.messages.job_failed')}
            </p>
          )}
          <ButtonGroup>
            <Button color="primary" variant="contained" onClick={this.handleDone}>
              {t('general:actions.done')}
            </Button>
          </ButtonGroup>
        </FormWrapper>
      </PageWrapper>
    );
  }

  render(): React.Element<*> {
    switch (this.controller.phase) {
      case 'previewing':
        return this.renderPreviewing();
      case 'preview':
        return this.renderPreview();
      case 'processing':
        return this.renderProcessing();
      case 'result':
        return this.renderResult();
      case 'upload':
      default:
        return this.renderUploadForm();
    }
  }
}
